import React, { FC, useRef } from 'react'
import { useTheme } from 'styled-components'

import {
  DefaultFeatureFlags,
  PRMarinaRect,
  useIsCameraMoving,
  useLockCamera,
  usePRFile,
  useStatus,
  useZoom,
} from '@sketch-hq/sketch-web-renderer'
import { ErrorMessage, LoadingPlaceholder } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import { WebRendererCanvas } from 'components/WebRendererCanvas'
import { useCanvasPresentationFile } from 'components/WebRendererCanvas/hooks'
import { ArtboardAnnotationOverlay } from 'modules/annotations/components'
import { DraftCommentProvider } from 'modules/annotations/components/DraftCommentContext'
import LoadingState from 'modules/shares/components/LoadingState'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'
import { ArtboardOriginContent } from '../ArtboardOriginContent'
import { useCanvasAnnotations } from 'modules/annotations/hooks'

import { InspectorState } from 'modules/shares/Inspector/types'

import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'
import { useSetupComponentDescriptions } from 'modules/shares/ArtboardDetailView/components/ArtboardDetailInspector/useEditableComponentDescription'
import { useMapPRMarinaRectToCanvasRelativeRect } from 'modules/shares/ArtboardDetailView/hooks/inspector/canvasPositionUtilsHooks'

import { OverlayGrid } from '../OverlayGrid'
import { useArtboardContext } from '../ArtboardContext'
import { OverlayLayout } from '../OverlayLayout'

import * as S from './ArtboardCanvas.styles'
import ArtboardPaginationControls from './ArtboardPaginationControls'
import { Pagination } from 'modules/shares/types'
import { useVersioning } from 'modules/versioning'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64'

const ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS: Partial<
  typeof DefaultFeatureFlags
> = {
  canvasLayersEvents: true,
}

interface ArtboardCanvasProps {
  pageUUID: string
  hasInspectorDataError: boolean
  inspectorDataIsReady: boolean
  inspector: InspectorState
  currentVersion: VersionFragment | undefined
  artboard: ArtboardDetailInfoFragment | undefined
  pagination: Pagination | undefined
  permanentArtboardShortId?: string
  shareIdentifier: string
}
export const ArtboardCanvas: FC<ArtboardCanvasProps> = ({
  pageUUID,
  inspectorDataIsReady,
  hasInspectorDataError,
  permanentArtboardShortId,
  inspector,
  currentVersion,
  artboard,
  pagination,
  shareIdentifier,
}) => {
  const theme = useTheme()
  const isMoving = useIsCameraMoving()
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const { versionShortId } = useVersioning()

  const artboardUUID = artboard!.uuid
  const isInspectorActive = inspector.isActive

  const isWaitingForInspectorData = isInspectorActive && !inspectorDataIsReady
  const isValidPagination =
    Number.isInteger(pagination?.current) && Number.isInteger(pagination?.total)

  const { isRendering, hasError, isLoading } = useCanvasPresentationFile({
    pageUUID,
  })

  const canvasStatus = useStatus()

  const prFile = usePRFile()
  const artboardNode = prFile?.getRootNode()
  const artboardBounds = artboardNode?.getAbsoluteExtent()

  const zoom = useZoom()
  const lockCamera = useLockCamera()

  const {
    activeAnnotation,
    isPlacingDraftAnnotation,
    ...pointerEvents
  } = useCanvasAnnotations({
    shareIdentifier,
    permanentPageId: pageUUID,
    permanentArtboardId: artboardUUID,
  })

  useSetupComponentDescriptions(shareIdentifier, artboard, currentVersion)

  if (isLoading) {
    return <LoadingState />
  }

  if (isRendering) {
    return (
      <ErrorMessage
        title="Rendering Canvas…"
        description="The canvas is rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    )
  }

  const page = artboard!.page

  if (hasError || !page || (isInspectorActive && hasInspectorDataError)) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the data. You can find more information
            on our <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  //
  // Only show it when the canvas is read but not the data
  const showInspectorLoadingDataOverlay =
    isInspectorActive &&
    canvasStatus.type === 'READY' &&
    isWaitingForInspectorData

  const isHoveringSelectableElement = Boolean(inspector.hoveredLayerId)

  return (
    <S.Wrapper
      ref={canvasContainerRef}
      onClick={() => {
        // Clicks on layers use e.stopPropagation()
        // If a click reaches here it means that the user clicked on the canvas
        // but outside any layer. Deselect the current one.
        if (inspector.selectedLayerId) {
          inspector.onClickLayer(undefined)
        }
      }}
    >
      <WebRendererCanvas
        isWaitingForMoreData={isWaitingForInspectorData}
        pageUUID={pageUUID}
        artboardUUID={artboardUUID}
        page={page}
        versionShortId={versionShortId}
        shareIdentifier={shareIdentifier}
        backgroundColor={theme.colors.background.secondary.B}
        featureFlags={ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS}
        webRenderCursor={getWebRenderCursor(
          isPlacingDraftAnnotation,
          isHoveringSelectableElement
        )}
        webRenderContainerProps={{
          ...pointerEvents,
        }}
      >
        {!IS_EMBEDDED && (
          <ArtboardOriginContent>
            <DraftCommentProvider>
              <ArtboardAnnotationOverlay
                shareIdentifier={shareIdentifier}
                permanentArtboardId={artboardUUID}
                artboardWidth={artboardBounds?.width ?? 0}
                artboardHeight={artboardBounds?.height ?? 0}
              />
            </DraftCommentProvider>
            {isInspectorActive && (
              <>
                {artboardBounds && (
                  <GridAndLayout artboardBounds={artboardBounds} />
                )}
                <S.StyledArtboardInspector
                  zoomFactor={zoom ?? 1}
                  inspector={inspector}
                  shareIdentifier={shareIdentifier}
                  permanentArtboardShortId={permanentArtboardShortId}
                  lockCamera={lockCamera}
                  isCameraMoving={Boolean(isMoving)}
                />
              </>
            )}
          </ArtboardOriginContent>
        )}
      </WebRendererCanvas>
      {/* We don't want this to child of WebRendererCanvas because isWaitingForMoreData prop
        will make the whole component to be blured but we want this overlay to be normal */}
      {showInspectorLoadingDataOverlay && (
        <S.LoadingOverlay datatest-id="loading-inspector">
          <LoadingPlaceholder dark={false} size="64px" />
          <span> Preparing Inspector… </span>
        </S.LoadingOverlay>
      )}
      {isValidPagination && (
        <ArtboardPaginationControls
          current={pagination!.current!}
          total={pagination!.total!}
          onlyKeyboardNavigation
        />
      )}
    </S.Wrapper>
  )
}

type GridAndLayoutProps = {
  artboardBounds: PRMarinaRect
}
function GridAndLayout({ artboardBounds }: GridAndLayoutProps) {
  const canvasRelativeRect = useMapPRMarinaRectToCanvasRelativeRect(
    artboardBounds
  )
  const {
    isGridEnabled,
    gridSize,
    thickGridTimes,
    isLayoutEnabled,
    layoutSettings,
  } = useArtboardContext()

  if (!canvasRelativeRect) {
    return null
  }
  return (
    <>
      <OverlayGrid
        isEnabled={isGridEnabled}
        gridSize={gridSize}
        thickLineStep={thickGridTimes}
      />
      <OverlayLayout
        isEnabled={isLayoutEnabled}
        artboardWidth={canvasRelativeRect.width}
        artboardHeight={canvasRelativeRect.height}
        layoutSettings={layoutSettings}
      />
    </>
  )
}

function getWebRenderCursor(
  isPlacingDraftAnnotation: boolean | undefined,
  isHoveringSelectableElement: boolean
) {
  if (isPlacingDraftAnnotation) {
    return 'copy'
  }

  if (isHoveringSelectableElement) {
    return 'auto'
  }

  return undefined
}
